import { useState, useEffect } from 'react';
import { X, TrendingUp, TrendingDown, DollarSign, Info, Gift, Landmark } from 'lucide-react';
import { balanceConsolidadoService } from '../../services/balanceConsolidadoService';
import { PeriodoBalance } from '../../services/balanceService';
import { Movimiento } from '../../lib/supabase';
import { DialogShell } from '../ui/DialogShell';
import { formatDate } from '../../utils/formatters';
import { getTipoBadges } from '../../utils/movimientoBadges';
import { calcularTotales } from '../../utils/balanceCalculations';
import { FondosCampanaTabla } from '../FondosCampanaTabla';
import { ComprobantesDeMovimiento } from '../comprobantes/ComprobantesDeMovimiento';
import { MontoCorregido } from '../comprobantes/MontoCorregido';

interface DesgloseEntidadModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly entidadId: string;
  readonly entidadNombre: string;
  readonly tipoEntidad: 'lumisial' | 'diocesis';
  readonly periodo: PeriodoBalance;
  readonly mostrarTodosMovimientos?: boolean;
}

interface MovimientoBadgesProps {
  readonly mov: Movimiento;
  readonly esNoBalance: boolean;
  readonly formatDate: (date: string) => string;
}

// La condición esDonacion para la sección de balance (badge emerald) usa la misma
// heurística que getTipoBadges — se mantiene inline aquí porque esa sección conserva
// su badge propio (emerald + ícono Gift), fuera del alcance de esta entrega.
const MovimientoBadges: React.FC<MovimientoBadgesProps> = ({ mov, esNoBalance, formatDate }) => {
  const esDonacion = mov.donacion_de_miembro_id != null || mov.donacion_de_nombre_externo != null ||
    (mov.origen_tipo === 'actividad' && mov.afecta_balance === false && mov.tipo === 'egreso');
  return (
    <div className="flex flex-wrap items-center gap-2 mt-0.5">
      <p className="text-xs text-slate-500">{formatDate(mov.fecha)}</p>
      {mov.categoria_aporte === 'cuota_iglesia' && (
        <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-slate-200 text-slate-600">
          {mov.modo_pago_cuota_iglesia == null ? 'JAL' : 'PA'}
        </span>
      )}
      {esNoBalance
        ? getTipoBadges(mov).map((b) => (
            <span key={b.label} className={`text-xs px-1.5 py-0.5 rounded font-medium ${b.clases}`}>
              {b.label}
            </span>
          ))
        : esDonacion && (
            <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-medium bg-emerald-100 text-emerald-700">
              <Gift className="w-3 h-3" />
              Donación
            </span>
          )}
    </div>
  );
};

export const DesgloseEntidadModal: React.FC<DesgloseEntidadModalProps> = ({
  isOpen,
  onClose,
  entidadId,
  entidadNombre,
  tipoEntidad,
  periodo,
  mostrarTodosMovimientos = false
}) => {
  const [loading, setLoading] = useState(true);
  const [movimientosBalance, setMovimientosBalance] = useState<Movimiento[]>([]);
  const [movimientosNoBalance, setMovimientosNoBalance] = useState<Movimiento[]>([]);
  const [totales, setTotales] = useState({ ingresos: 0, egresos: 0, financiamiento: 0, balance: 0 });
  // Se fija al cargar el desglose: el plazo de cada comprobante se mide contra este instante.
  const [ahora, setAhora] = useState(() => new Date());

  useEffect(() => {
    if (isOpen && entidadId) {
      loadDesglose();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entidadId, periodo]);

  const loadDesglose = async () => {
    try {
      setLoading(true);
      if (mostrarTodosMovimientos) {
        const data = await balanceConsolidadoService.getMovimientosCompletosEntidad(
          entidadId, tipoEntidad, periodo.mes || 1, periodo.año
        );
        setMovimientosBalance(data.movimientos_balance);
        setMovimientosNoBalance(data.movimientos_no_balance);
        setTotales(calcularTotales(data.movimientos_balance));
      } else {
        const data = await balanceConsolidadoService.getDesgloseEntidad(
          entidadId, tipoEntidad, periodo.mes || 1, periodo.año
        );
        setMovimientosBalance([...data.ingresos, ...data.egresos]);
        setMovimientosNoBalance([]);
        setTotales({
          ingresos: data.total_ingresos,
          egresos: data.total_egresos,
          financiamiento: data.total_financiamiento,
          balance: data.balance_neto,
        });
      }
      setAhora(new Date());
    } catch {
      // error silenciado — UI queda en estado vacío
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);

  const getPeriodoLabel = () => {
    if (periodo.tipo === 'mensual' && periodo.mes) {
      const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                     'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      return `${meses[periodo.mes - 1]} ${periodo.año}`;
    }
    return `Año ${periodo.año}`;
  };

  const renderMovimientos = (movimientos: Movimiento[], titulo: string, esNoBalance: boolean = false) => {
    if (movimientos.length === 0) return null;
    const ingresos = movimientos.filter(m => m.tipo === 'ingreso');
    const egresos = movimientos.filter(m => m.tipo === 'egreso');

    return (
      <div className={`${esNoBalance ? 'bg-amber-50 border border-amber-200 rounded-lg p-4' : ''}`}>
        <h4 className={`font-semibold mb-3 ${esNoBalance ? 'text-amber-900' : 'text-slate-700'}`}>{titulo}</h4>
        {esNoBalance && (
          <div className="flex items-start gap-2 mb-4 text-amber-800">
            <Info className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <p className="text-sm">
              Estos movimientos son fondos de paso (campañas, cuota iglesia, etc.) y no afectan el balance disponible.
            </p>
          </div>
        )}
        {ingresos.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-slate-700">Ingresos</span>
            </div>
            <div className="space-y-2">
              {ingresos.map((mov) => (
                <div key={mov.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{mov.descripcion || 'Sin descripción'}</p>
                    <MovimientoBadges mov={mov} esNoBalance={esNoBalance} formatDate={formatDate} />
                    <div className="mt-1">
                      <ComprobantesDeMovimiento movimiento={mov} ahora={ahora} />
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-emerald-600">{formatCurrency(mov.monto)}</span>
                    {mov.monto_original != null && <MontoCorregido montoOriginal={Number(mov.monto_original)} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {egresos.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-slate-700">Egresos</span>
            </div>
            <div className="space-y-2">
              {egresos.map((mov) => (
                <div key={mov.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{mov.descripcion || 'Sin descripción'}</p>
                    <MovimientoBadges mov={mov} esNoBalance={esNoBalance} formatDate={formatDate} />
                    <div className="mt-1">
                      <ComprobantesDeMovimiento movimiento={mov} ahora={ahora} />
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-red-600">{formatCurrency(mov.monto)}</span>
                    {mov.monto_original != null && <MontoCorregido montoOriginal={Number(mov.monto_original)} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      header={
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 flex items-center justify-between p-6">
          <div>
            <h3 className="text-2xl font-bold text-slate-800">{entidadNombre}</h3>
            <p className="text-sm text-slate-600 mt-1">Desglose de movimientos - {getPeriodoLabel()}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Cerrar modal"
          >
            <X className="w-6 h-6 text-slate-600" />
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
        </div>
      ) : (
        <>
          <div className={`-mx-6 -mt-6 mb-4 p-6 bg-slate-50 border-b border-slate-200 grid gap-4 ${totales.financiamiento > 0 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'}`}>
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-medium text-slate-600">Ingresos</span>
              </div>
              <p className="text-xl font-bold text-emerald-600">{formatCurrency(totales.ingresos)}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-4 h-4 text-red-600" />
                <span className="text-xs font-medium text-slate-600">Egresos</span>
              </div>
              <p className="text-xl font-bold text-red-600">{formatCurrency(totales.egresos)}</p>
            </div>
            {totales.financiamiento > 0 && (
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-center gap-2 mb-1">
                  <Landmark className="w-4 h-4 text-teal-600" />
                  <span className="text-xs font-medium text-slate-600">Financiamiento recibido</span>
                </div>
                <p className="text-xl font-bold text-teal-700">{formatCurrency(totales.financiamiento)}</p>
              </div>
            )}
            <div className={`rounded-lg p-4 border ${totales.balance >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className={`w-4 h-4 ${totales.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
                <span className="text-xs font-medium text-slate-600">Balance</span>
              </div>
              <p className={`text-xl font-bold ${totales.balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                {formatCurrency(totales.balance)}
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {movimientosBalance.length === 0 && movimientosNoBalance.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No hay movimientos para el período seleccionado
              </div>
            ) : (
              <>
                {renderMovimientos(movimientosBalance, 'Movimientos que afectan el balance')}
                {mostrarTodosMovimientos && movimientosNoBalance.length > 0 && (
                  <div className="pt-4">
                    {renderMovimientos(movimientosNoBalance, 'Otros movimientos del período', true)}
                  </div>
                )}
              </>
            )}
          </div>

          <FondosCampanaTabla ownerContext={{ tipo: tipoEntidad, id: entidadId, nombre: entidadNombre }}
            puedeOperar={false} periodo={periodo} mostrarEnvios={false} />
          <div className="-mx-6 -mb-6 mt-6 sticky bottom-0 bg-white border-t border-slate-200 p-4 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium"
            >
              Cerrar
            </button>
          </div>
        </>
      )}
    </DialogShell>
  );
};
